import { useCallback, useEffect, useRef } from 'react';
import { Animated, GestureResponderHandlers, Keyboard, PanResponder } from 'react-native';
import { GESTURE_EXIT_VELOCITY } from '@shared/styles';
import { useKeyboardVisible } from '@shared/util/hooks';
import { SheetSize } from './props';

export function didFlingUp(vy: number): boolean {
  return vy < -GESTURE_EXIT_VELOCITY;
}
export function didFlingDown(vy: number): boolean {
  return vy > GESTURE_EXIT_VELOCITY;
}

const GESTURE_START_HORIZONTAL_TOLERANCE = 4;

export function useSwipeGesture(
  initialTop: number,
  fullTop: number,
  onSwipeClose: () => void,
  onStartSwipe: () => void,
  initialSize: SheetSize,
): {
  panHandlers: GestureResponderHandlers;
  top: Animated.AnimatedValue;
} {
  const keyboardVisible = useKeyboardVisible();
  const mode = useRef<'half' | 'full'>('half');
  const top = useRef<Animated.AnimatedValue>(new Animated.Value(initialTop)).current;

  const toFull = useCallback(() => {
    Animated.spring(top, { toValue: fullTop, useNativeDriver: false }).start(() => {
      mode.current = 'full';
    });
  }, [top, fullTop]);
  const toFullFast = useCallback(() => {
    Animated.spring(top, { toValue: fullTop, speed: 24, useNativeDriver: false }).start(() => {
      mode.current = 'full';
    });
  }, [top, fullTop]);
  const toHalf = useCallback(() => {
    Keyboard.dismiss();
    Animated.spring(top, { toValue: initialTop, useNativeDriver: false }).start(() => {
      mode.current = 'half';
    });
  }, [top, initialTop]);

  useEffect(() => {
    if (keyboardVisible) {
      toFullFast();
    }
  }, [keyboardVisible, toFullFast]);

  useEffect(() => {
    if (initialSize === 'full') {
      toFull();
    }
  }, [initialSize, toFull]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_event, gestureState) =>
        Math.abs(gestureState.dy) >= 1 &&
        Math.abs(gestureState.dx) < GESTURE_START_HORIZONTAL_TOLERANCE,
      onPanResponderGrant: () => {
        onStartSwipe();
        top.stopAnimation();
        top.setOffset((top as any)._value);
      },
      onPanResponderMove: (_event, gestureState) => top.setValue(gestureState.dy),
      onPanResponderRelease: (_event, gestureState) => {
        const { dy, vy } = gestureState;
        if (dy === 0 && vy === 0) {
          return;
        }
        top.flattenOffset();
        if (mode.current === 'half') {
          if (dy < -initialTop / 2 || didFlingUp(vy)) {
            // HALF -> FULL
            toFull();
          } else if (dy > initialTop / 2 || didFlingDown(vy)) {
            // HALF -> CLOSE
            onSwipeClose();
          } else {
            // HALF -> RESET
            toHalf();
          }
        } else if (mode.current === 'full') {
          if (dy > initialTop) {
            // FULL -> CLOSE
            onSwipeClose();
          } else if (dy > initialTop / 2 || didFlingDown(vy)) {
            // FULL -> HALF
            toHalf();
          } else {
            // FULL -> RESET
            toFull();
          }
        }
      },
    }),
  ).current;

  return {
    panHandlers: panResponder.panHandlers,
    top,
  };
}
